// packages/ui/src/ws/wsClient.ts
//
// Browser WebSocket client implementing CONTEXT (lifecycle)
// (ws-ticket flow), (heartbeat + limits), (origin enforcement
// — server-side, mirrored here only via wsUrl returned from backend).
//
// Wire contract (canonical, see API-CONTRACTS.md WS /ws Block 2):
//   - Auth: POST /api/v1/auth/ws-ticket → { ticket, wsUrl, expiresAt }; client
//     opens `${wsUrl}?ticket=${ticket}` immediately (Redis GETDEL → 30s TTL).
//   - Server → client envelope: discriminated union on `type`:
//       'connected' | 'subscribe_ack' | 'subscribe_denied' | 'event' | 'pong'
//       | 'error'.
//     Domain events (auth.session.logout, auth.permissions.updated,
//     notification.create, ...) ride INSIDE `type:'event'` with
//     `eventName: string` and `payload: Record<string, unknown>`.
//   - Client → server: `subscribe`/`unsubscribe`/`ping` with monotonic seq.
//
// Reconnect policy:
//   Exponential backoff 1s → 2s → 4s → 8s → 16s, cap 30s, ±20% jitter, retry
//   forever. Auth cookie remains valid across reconnects (HttpOnly cookie
//   plus fresh ticket each time).
//
// Heartbeat:
//   Client emits `{type:'ping', seq}` every 25s. If no `{type:'pong', seq}`
//   within 5s, force `socket.close(4000)` and reconnect. (Server independently
//   pings every 30s and closes after 3 missed pongs — close code 4002 / 4004.)

import { resolveApiBaseUrl } from '@printway/shared';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type WsScope = 'merchant' | 'vendor' | 'admin';

export interface ConnectWsOptions {
  /** Portal scope this client is logging in from. */
  scope: WsScope;
  /** Required for `merchant`/`vendor` scopes — current org/merchant id. */
  orgId?: string;
  /**
   * Override for the ticket endpoint. Defaults to `/api/v1/auth/ws-ticket`
   * resolved against the api portal via `getPortalUrl('api', ...)`.
   * Tests / SSR can inject a stub URL.
   */
  ticketUrl?: string;
  /**
   * Override for the WS URL. Normally taken from the ticket response (`wsUrl`
   * field) — set here for tests against a local mock.
   */
  wsUrlOverride?: string;
  /** Heartbeat interval (ms). Default 25s. */
  heartbeatIntervalMs?: number;
  /** Heartbeat timeout — close + reconnect if no pong in this window. Default 5s. */
  heartbeatTimeoutMs?: number;
  /** Initial reconnect delay before backoff (ms). Default 1000. */
  initialReconnectMs?: number;
  /** Max reconnect delay cap (ms). Default 30000. */
  maxReconnectMs?: number;
  /** Optional logger; defaults to no-op. */
  logger?: WsLogger;
  /** Fetch implementation override (tests / SSR). */
  fetchImpl?: typeof fetch;
  /** WebSocket constructor override (tests). */
  wsImpl?: typeof WebSocket;
}

export interface WsLogger {
  info: (msg: string, meta?: Record<string, unknown>) => void;
  warn: (msg: string, meta?: Record<string, unknown>) => void;
  error: (msg: string, meta?: Record<string, unknown>) => void;
}

/**
 * Server → client domain event payload (from `type:'event'` envelope).
 *
 * Receivers cast `payload` based on `eventName` — runtime validation lives in
 * handlers.ts where each event name has a known shape.
 */
export interface WsServerEvent {
  channel: string;
  eventName: string;
  payload: Record<string, unknown>;
  seq: number;
}

/**
 * Lifecycle / domain events emitted by the client. `'open'` and `'close'`
 * fire whenever the underlying WebSocket transitions; domain handlers
 * registered via `client.on(eventName, ...)` fire only for `type:'event'`
 * envelopes that match `eventName`.
 */
export type WsLifecycleEvent =
  | { type: 'open' }
  | { type: 'close'; code: number; reason: string }
  | { type: 'error'; message: string };

export type WsEventHandler = (event: WsServerEvent) => void;
export type WsLifecycleHandler = (event: WsLifecycleEvent) => void;

export interface WsClientHandle {
  /** Subscribe to a server channel (e.g. `ws:user:{userId}`, `ws:merchant:{id}`). */
  subscribe: (channel: string) => void;
  /** Unsubscribe from a server channel. */
  unsubscribe: (channel: string) => void;
  /** Register a handler for a specific `eventName` (auth.session.logout, …). */
  on: (eventName: string, handler: WsEventHandler) => () => void;
  /** Register a lifecycle handler — `'open' | 'close' | 'error'`. */
  onLifecycle: (handler: WsLifecycleHandler) => () => void;
  /** Tear down — closes socket, cancels timers, blocks reconnect. */
  disconnect: () => void;
  /** Inspect current connection state (for tests / debug UI). */
  getState: () => WsConnectionState;
}

export type WsConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const NOOP_LOGGER: WsLogger = {
  info: () => {},
  warn: () => {},
  error: () => {},
};

interface WsTicketResponse {
  success: true;
  data: {
    ticket: string;
    wsUrl: string;
    expiresAt: string;
  };
  requestId: string;
}

/**
 * Compute the next backoff delay with ±20% jitter.
 *
 * Sequence (without jitter): 1s, 2s, 4s, 8s, 16s, 30s, 30s, ... (cap).
 * Jitter prevents thundering-herd when many tabs reconnect simultaneously.
 */
function nextBackoffMs(
  attempt: number,
  initialMs: number,
  capMs: number,
): number {
  const base = Math.min(initialMs * Math.pow(2, attempt), capMs);
  const jitterFactor = 0.8 + Math.random() * 0.4; // [0.8, 1.2)
  return Math.round(base * jitterFactor);
}

/**
 * Read the ticket endpoint response. Throws on network / non-200 / shape
 * mismatch — the caller catches and schedules reconnect.
 */
/**
 * Read the `XSRF-TOKEN` cookie value (server-readable, not HttpOnly) so we
 * can echo it back as the `X-XSRF-TOKEN` header for the double-submit CSRF
 * check enforced by `@fastify/csrf-protection`. Mirrors the
 * helper inside `@printway/shared`'s `apiClient`; duplicated here because
 * `wsClient` runs in the browser without the apiClient wrapper.
 */
function readXsrfCookieFromDocument(): string | undefined {
  const doc = (globalThis as { document?: { cookie?: string } }).document;
  if (!doc || typeof doc.cookie !== 'string') return undefined;
  for (const part of doc.cookie.split('; ')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq) === 'XSRF-TOKEN') {
      try {
        return decodeURIComponent(part.slice(eq + 1));
      } catch {
        return part.slice(eq + 1);
      }
    }
  }
  return undefined;
}

async function fetchWsTicket(opts: {
  ticketUrl: string;
  scope: WsScope;
  orgId?: string;
  fetchImpl: typeof fetch;
}): Promise<WsTicketResponse['data']> {
  const body: Record<string, string> = { scope: opts.scope };
  if (opts.orgId) body['org_id'] = opts.orgId;

  // CSRF double-submit. Server enforces on POST when the
  // cookie is present (i.e. authenticated session). Inject directly here
  // — the default `fetchImpl` is `globalThis.fetch`, which does NOT carry
  // the apiClient interceptor that normally adds this header.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const xsrf = readXsrfCookieFromDocument();
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;

  const res = await opts.fetchImpl(opts.ticketUrl, {
    method: 'POST',
    credentials: 'include', // HttpOnly auth cookie
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`ws-ticket HTTP ${res.status}`);
  }
  const json = (await res.json()) as Partial<WsTicketResponse>;
  if (
    !json ||
    json.success !== true ||
    !json.data ||
    typeof json.data.ticket !== 'string' ||
    typeof json.data.wsUrl !== 'string'
  ) {
    throw new Error('ws-ticket malformed response');
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Open a managed WebSocket connection with auto-reconnect, heartbeat, and
 * domain event dispatch. Idempotent across HMR — call `disconnect()` to tear
 * down before reopening with new options.
 */
export function connectWs(opts: ConnectWsOptions): WsClientHandle {
  const logger = opts.logger ?? NOOP_LOGGER;
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const WSImpl = opts.wsImpl ?? globalThis.WebSocket;
  const heartbeatIntervalMs = opts.heartbeatIntervalMs ?? 25_000;
  const heartbeatTimeoutMs = opts.heartbeatTimeoutMs ?? 5_000;
  const initialReconnectMs = opts.initialReconnectMs ?? 1_000;
  const maxReconnectMs = opts.maxReconnectMs ?? 30_000;
  // Build ticket URL via `resolveApiBaseUrl()` (reads VITE_API_URL /
  // VITE_API_BASE_URL — the same env path used by `apiClient`) instead of
  // `getPortalUrl('api', ...)` which reads a separate VITE_PORTAL_URL_API
  // that is not set on sandbox/staging deploys, causing the URL to fall
  // through to the production-only host `api.printway.com` and trigger
  // `ERR_NAME_NOT_RESOLVED` in the browser.
  const ticketUrl =
    opts.ticketUrl ??
    `${resolveApiBaseUrl().replace(/\/$/, '')}/api/v1/auth/ws-ticket`;

  // ----- mutable state -----
  let socket: WebSocket | null = null;
  let state: WsConnectionState = 'idle';
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let supersededResume: (() => void) | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let pongTimer: ReturnType<typeof setTimeout> | null = null;
  let nextSeq = 1;
  let lastPingSeq = 0;
  let disposed = false;
  // Channels we've asked the server to subscribe us to. On reconnect we
  // re-issue these (server has no session memory for ws-ticket flows).
  const desiredChannels = new Set<string>();
  // Domain event handlers, keyed by eventName.
  const eventHandlers = new Map<string, Set<WsEventHandler>>();
  const lifecycleHandlers = new Set<WsLifecycleHandler>();

  // ----- internals -----
  const emitLifecycle = (ev: WsLifecycleEvent) => {
    for (const h of lifecycleHandlers) {
      try {
        h(ev);
      } catch (err) {
        logger.warn('[wsClient] lifecycle handler threw', {
          err: String(err),
        });
      }
    }
  };

  const dispatchEvent = (ev: WsServerEvent) => {
    const handlers = eventHandlers.get(ev.eventName);
    if (!handlers || handlers.size === 0) return;
    for (const h of handlers) {
      try {
        h(ev);
      } catch (err) {
        logger.warn('[wsClient] event handler threw', {
          eventName: ev.eventName,
          err: String(err),
        });
      }
    }
  };

  const clearHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (pongTimer) {
      clearTimeout(pongTimer);
      pongTimer = null;
    }
  };

  const sendJson = (payload: unknown) => {
    if (!socket || socket.readyState !== WSImpl.OPEN) return;
    try {
      socket.send(JSON.stringify(payload));
    } catch (err) {
      logger.warn('[wsClient] send failed', { err: String(err) });
    }
  };

  const startHeartbeat = () => {
    clearHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (!socket || socket.readyState !== WSImpl.OPEN) return;
      lastPingSeq = nextSeq++;
      sendJson({ type: 'ping', seq: lastPingSeq });
      // Arm pong watchdog. If pong with matching seq arrives in time the
      // message handler clears it.
      if (pongTimer) clearTimeout(pongTimer);
      pongTimer = setTimeout(() => {
        logger.warn('[wsClient] pong timeout — forcing reconnect', {
          seq: lastPingSeq,
        });
        // Close with a non-1000 code so the close handler treats this as
        // anomalous and schedules a reconnect.
        try {
          socket?.close(4000, 'pong-timeout');
        } catch {
          // ignore
        }
      }, heartbeatTimeoutMs);
    }, heartbeatIntervalMs);
  };

  const scheduleReconnect = (reason: string) => {
    if (disposed) return;
    if (reconnectTimer) return; // already armed
    state = 'reconnecting';
    const delay = nextBackoffMs(
      reconnectAttempt,
      initialReconnectMs,
      maxReconnectMs,
    );
    logger.info('[wsClient] scheduling reconnect', {
      attempt: reconnectAttempt,
      delay,
      reason,
    });
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectAttempt += 1;
      // Fire and forget — `connect` handles its own errors.
      void connect();
    }, delay);
  };

  const handleMessage = (raw: MessageEvent<string | ArrayBuffer | Blob>) => {
    if (typeof raw.data !== 'string') {
      // Phase 1 server only sends JSON text frames; ignore binary.
      return;
    }
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw.data) as Record<string, unknown>;
    } catch {
      logger.warn('[wsClient] non-JSON message dropped');
      return;
    }
    const type = msg['type'];
    if (typeof type !== 'string') return;

    switch (type) {
      case 'connected': {
        // Re-issue subscribe for every desired channel so reconnect restores
        // the subscription set without app-level refresh.
        for (const ch of desiredChannels) {
          sendJson({ type: 'subscribe', channel: ch });
        }
        return;
      }
      case 'subscribe_ack':
        return;
      case 'subscribe_denied': {
        logger.warn('[wsClient] subscribe_denied', {
          channel: msg['channel'],
          code: msg['code'],
        });
        return;
      }
      case 'pong': {
        // Pong cancels the watchdog regardless of seq match — the server
        // echoes our seq, but a single in-flight ping is the common case.
        if (pongTimer) {
          clearTimeout(pongTimer);
          pongTimer = null;
        }
        return;
      }
      case 'error': {
        logger.warn('[wsClient] server error frame', {
          code: msg['code'],
          message: msg['message'],
        });
        emitLifecycle({
          type: 'error',
          message: String(msg['message'] ?? msg['code'] ?? 'ws-server-error'),
        });
        return;
      }
      case 'event': {
        const channel = msg['channel'];
        const eventName = msg['eventName'];
        const payload = msg['payload'];
        const seq = msg['seq'];
        if (
          typeof channel !== 'string' ||
          typeof eventName !== 'string' ||
          typeof seq !== 'number' ||
          payload === undefined ||
          payload === null ||
          typeof payload !== 'object'
        ) {
          logger.warn('[wsClient] malformed event frame');
          return;
        }
        dispatchEvent({
          channel,
          eventName,
          payload: payload as Record<string, unknown>,
          seq,
        });
        return;
      }
      default:
        // Unknown message type — forward-compat: log + drop.
        logger.warn('[wsClient] unknown message type', { type });
    }
  };

  // Server evicted this socket because a newer tab of the same user
  // connected (4001 superseded_by_new_connection). Backoff-reconnecting
  // here would just evict the newer tab back — N>cap tabs would rotate a
  // dead socket between them forever. Instead stay dormant and reconnect
  // once when THIS tab regains focus/visibility (making it the newest).
  const SUPERSEDED_CLOSE_CODE = 4001;

  const armSupersededResume = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      // Non-browser embedding (tests/node) — fall back to normal backoff.
      scheduleReconnect('superseded-no-dom');
      return;
    }
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      // Evicted while actively visible (multi-window) — normal backoff so
      // this window still recovers without user interaction.
      scheduleReconnect('superseded-while-visible');
      return;
    }
    if (supersededResume) return; // already armed
    state = 'reconnecting';
    logger.info('[wsClient] superseded by newer connection — dormant until focus');
    const resume = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
      supersededResume = null;
      reconnectAttempt = 0;
      void connect();
    };
    supersededResume = resume;
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
  };

  const handleClose = (code: number, reason: string) => {
    clearHeartbeat();
    socket = null;
    if (disposed) {
      state = 'closed';
      emitLifecycle({ type: 'close', code, reason });
      return;
    }
    emitLifecycle({ type: 'close', code, reason });
    if (code === SUPERSEDED_CLOSE_CODE) {
      armSupersededResume();
      return;
    }
    // 1000 = normal closure (client called disconnect via close path)
    // — but we set state='closed' on disposal already, so any other close
    // means anomalous → reconnect.
    scheduleReconnect(`close-${code}`);
  };

  async function connect(): Promise<void> {
    if (disposed) return;
    state = 'connecting';
    try {
      const ticket = await fetchWsTicket({
        ticketUrl,
        scope: opts.scope,
        ...(opts.orgId ? { orgId: opts.orgId } : {}),
        fetchImpl,
      });
      const wsUrl = opts.wsUrlOverride ?? ticket.wsUrl;
      const fullUrl = `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}ticket=${encodeURIComponent(ticket.ticket)}`;
      const next = new WSImpl(fullUrl);
      socket = next;

      next.addEventListener('open', () => {
        if (disposed) {
          try {
            next.close(1000, 'disposed');
          } catch {
            // ignore
          }
          return;
        }
        state = 'open';
        reconnectAttempt = 0; // reset backoff on successful open
        startHeartbeat();
        emitLifecycle({ type: 'open' });
        logger.info('[wsClient] open', { scope: opts.scope });
      });

      next.addEventListener('message', handleMessage);

      next.addEventListener('error', () => {
        // The browser doesn't expose error details for security reasons —
        // close handler will fire next with a meaningful code.
        emitLifecycle({ type: 'error', message: 'ws-error' });
      });

      next.addEventListener('close', (ev) => {
        handleClose(ev.code, ev.reason);
      });
    } catch (err) {
      logger.warn('[wsClient] connect failed', { err: String(err) });
      // No socket to close — go straight to reconnect.
      scheduleReconnect(`connect-error: ${String(err)}`);
    }
  }

  // Kick off initial connect on next tick so handle is returned before any
  // sync handler fires.
  Promise.resolve().then(() => {
    void connect();
  });

  // ----- public handle -----
  const handle: WsClientHandle = {
    subscribe(channel: string) {
      desiredChannels.add(channel);
      if (state === 'open') {
        sendJson({ type: 'subscribe', channel });
      }
      // Otherwise: deferred — `connected` handler re-issues on (re)open.
    },
    unsubscribe(channel: string) {
      desiredChannels.delete(channel);
      if (state === 'open') {
        sendJson({ type: 'unsubscribe', channel });
      }
    },
    on(eventName: string, handler: WsEventHandler) {
      let bucket = eventHandlers.get(eventName);
      if (!bucket) {
        bucket = new Set();
        eventHandlers.set(eventName, bucket);
      }
      bucket.add(handler);
      return () => {
        const set = eventHandlers.get(eventName);
        if (!set) return;
        set.delete(handler);
        if (set.size === 0) eventHandlers.delete(eventName);
      };
    },
    onLifecycle(handler: WsLifecycleHandler) {
      lifecycleHandlers.add(handler);
      return () => {
        lifecycleHandlers.delete(handler);
      };
    },
    disconnect() {
      if (disposed) return;
      disposed = true;
      state = 'closed';
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (supersededResume) {
        document.removeEventListener('visibilitychange', supersededResume);
        window.removeEventListener('focus', supersededResume);
        supersededResume = null;
      }
      clearHeartbeat();
      if (socket) {
        try {
          socket.close(1000, 'client-disconnect');
        } catch {
          // ignore
        }
        socket = null;
      }
      eventHandlers.clear();
      lifecycleHandlers.clear();
      desiredChannels.clear();
    },
    getState() {
      return state;
    },
  };

  return handle;
}
